// src/pages/driver_group/DriverGroupEditPage.js
import React, { useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Box, 
  Typography, 
  Alert,
  Breadcrumbs,
  Link,
  Button,
  Snackbar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getCompany } from '../../api/companyAPI';
import { getDriverGroup, updateDriverGroup } from '../../api/driverGroupAPI';
import DriverGroupForm from '../../components/driver_group/DriverGroupForm';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';

const DriverGroupEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const driverGroupId = parseInt(id);

  // Fetch driver group details
  const { data: driverGroup, isLoading: isLoadingDriverGroup, isError, error } = useQuery({
    queryKey: ['driver-group', driverGroupId],
    queryFn: () => getDriverGroup(driverGroupId),
    enabled: !!driverGroupId && !isNaN(driverGroupId)
  });

  // Fetch company details once we have the driver group
  const { data: company, isLoading: isLoadingCompany } = useQuery({
    queryKey: ['company', driverGroup?.DriversGroupCompanyId],
    queryFn: () => getCompany(driverGroup.DriversGroupCompanyId),
    enabled: !!driverGroup?.DriversGroupCompanyId
  });

  // Update driver group mutation
  const mutation = useMutation({
    mutationFn: (data) => updateDriverGroup(driverGroupId, data),
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['driver-group', driverGroupId] });
      if (driverGroup?.DriversGroupCompanyId) {
        queryClient.invalidateQueries({ queryKey: ['driver-groups', driverGroup.DriversGroupCompanyId] });
      }
      
      setSnackbar({
        open: true,
        message: 'Driver group updated successfully!',
        severity: 'success'
      });
      
      // Navigate back to driver group details after a brief delay
      setTimeout(() => {
        navigate(`/driver_groups/${driverGroupId}`);
      }, 1500);
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error updating driver group: ${error.message}`,
        severity: 'error'
      });
    }
  });

  const handleSubmit = async (data) => {
    try {
      await mutation.mutateAsync(data);
    } catch (err) {
      // Error is handled by the mutation
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (isLoadingDriverGroup) {
    return <LoadingSpinner />;
  }

  if (isError || !driverGroup) {
    return <ErrorAlert message={error instanceof Error ? error.message : 'Error loading driver group'} />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          {/* <Breadcrumbs aria-label="breadcrumb" mb={1}>
            <Link component={RouterLink} to="/" color="inherit">
              Dashboard
            </Link>
            <Link component={RouterLink} to="/driver_groups" color="inherit">
              Driver Groups
            </Link>
            <Link 
              component={RouterLink} 
              to={`/driver_groups/${driverGroupId}`} 
              color="inherit"
            >
              {driverGroup.DriversGroupName}
            </Link>
            <Typography color="textPrimary">Edit</Typography>
          </Breadcrumbs> */}
          <Typography variant="h4">Edit {driverGroup.DriversGroupName}</Typography>
        </div>
        
        <Button 
          component={RouterLink} 
          to={`/driver_groups/${driverGroupId}`} 
          startIcon={<ArrowBackIcon />}
        >
          Back to Driver Group Details
        </Button>
      </Box>

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {mutation.error.message}
        </Alert>
      )}

      <DriverGroupForm 
        driverGroup={driverGroup}
        companyId={driverGroup.DriversGroupCompanyId}
        onSubmit={handleSubmit} 
        isLoading={mutation.isPending}
      />

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DriverGroupEditPage;